import { AxiosError } from 'axios';
import { startOfToday } from 'date-fns';

import type { FilterQueryData, FiltersConjunctionType } from '@components/filters';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { toQueryString } from '@utils/requests';

import type { Job, JobQuestionMultiLanguage, JobStatistics, JobTitleSearch } from 'types/jobs';
import type { ILimitSearchResult } from 'types/search';
import type { ITODO } from 'types/todo';
import { IUserRole } from 'types/users';

import { request, unhandledRequest } from '../request';

export type JobOrderingData = {
  id: number;
  title: string;
  order: number;
  pinned: boolean;
};

export type SubmitError = AxiosError<{
  ordering: string;
  error: string;
}>;

export type ApplicationFormFields = {
  includeCoverLetter: boolean;
  includeCv: boolean;
  includePhone: boolean;
  includeSsn: boolean;
  requireCoverLetter: boolean;
  requireCv: boolean;
  requirePhone: boolean;
  requireSex: boolean;
  requireSsn: boolean;
};

export type LanguageData = {
  code: CareerSiteLanguageType;
  isEnabled: boolean;
  hasApplicants: boolean;
};

export type JobApplicationForm = {
  questions: JobQuestionMultiLanguage[];
  details: ApplicationFormFields;
  languages: LanguageData[];
};

export type ApplicationformData = {
  questions?: Partial<JobQuestionMultiLanguage[]>;
  details?: Partial<ApplicationFormFields>;
  languages?: LanguageData[];
};

export type ExportApplicantsFileType = 'csv' | 'xlsx' | 'zip';

export type ExportFieldsType =
  // Applicant details
  | 'application_id'
  | 'application_name'
  | 'application_email'
  | 'application_phone'
  | 'application_ssn'
  | 'application_age'
  | 'application_gender'
  // Applicant job status
  | 'current_stage'
  | 'is_rejected'
  | 'is_notified_of_rejection'
  | 'is_hired'
  | 'is_retracted'
  | 'is_manually_added'
  | 'source'
  | 'number_of_reviews'
  | 'number_of_notes'
  // Dates
  | 'date_applied'
  | 'date_of_hire'
  | 'date_of_rejection'
  // Application question responses
  | 'application_question_responses';

export type checkedFields = ExportFieldsType[];

export type ApplicantFolderNamingTypes =
  | 'application_name'
  | 'application_ssn'
  | 'job_name'
  | 'application_job_stage'
  | 'national_registry_name'
  | 'national_registry_ssn';

export type ExportApplicantsData = {
  jobId: number;
  exportType: ExportApplicantsFileType;
  fields: checkedFields;
  folderNaming?: ApplicantFolderNamingTypes[];
  applicationIds?: number[];
};

export async function getApplicationForm(jobId: number) {
  const response = await unhandledRequest.get<JobApplicationForm>({
    url: `/jobs/${jobId}/applicationform/`,
  });
  return response.data;
}

export async function patchApplicationForm(jobId: number, data: ApplicationformData) {
  return unhandledRequest.post({
    url: `jobs/${jobId}/applicationform/`,
    data: data,
  });
}

export async function create(data: ITODO) {
  return unhandledRequest.post<Partial<Job>>({
    url: '/jobs/',
    data,
  });
}

export function edit(jobId: number, data: ITODO) {
  return request.patch({
    url: `/jobs/${jobId}/`,
    data,
  });
}

export function renameStage(jobId: number, stageId: number, name: string) {
  return request.post({
    url: `/jobs/${jobId}/stages/${stageId}/rename/`,
    data: { name },
  });
}

export function addStage(jobId: number, name: string, order: number) {
  return request.post({
    url: `/jobs/${jobId}/stages/`,
    data: { name, order },
  });
}

export function removeStage(jobId: number, stageId: number) {
  return request.delete({
    url: `/jobs/${jobId}/stages/${stageId}/remove/`,
  });
}

export function moveStage(jobId: number, stageId: number, order: number) {
  return request.post({
    url: `/jobs/${jobId}/stages/${stageId}/move/`,
    data: {
      order,
    },
  });
}

export function followers(jobId: number, data: ITODO) {
  return request.put({
    url: `/jobs/${jobId}/followers/`,
    data,
  });
}

export type FollowerUser = {
  departments: {
    id: number;
    translations: {
      language: CareerSiteLanguageType;
      title: string;
    }[];
  }[];
  email: string;
  id: number;
  name: string;
  picture: string;
  role: IUserRole;
};

export type ApplicationFollower = FollowerUser & { numApplications?: number };

export type Follower = FollowerUser;

export type FollowerResponse = {
  appFollowers: ApplicationFollower[];
  followers: Follower[];
};

export async function activeFollowers(jobId: number) {
  const response = await unhandledRequest.get<FollowerResponse>({
    url: `/jobs/${jobId}/followers/`,
  });
  return response.data;
}

export function remove(jobId: number) {
  return request.delete({
    url: `/jobs/${jobId}/`,
  });
}

export function loadFromDb(jobId: number) {
  return request.get({
    url: `/jobs/${jobId}/`,
  });
}

export async function loadFromDbUnhandled(jobId: number) {
  const response = await unhandledRequest.get<Job>({
    url: `/jobs/${jobId}/`,
  });
  return response.data;
}

export function loadJob(jobId: number, companyId?: number) {
  return request.get({
    url: '/search/jobs/' + toQueryString({ id: jobId, company_id: companyId ?? null }),
  });
}

export type SocialData = {
  image: string;
  title: string;
  description: string;
};

export async function getSocial(jobId: number) {
  const response = await unhandledRequest.get<SocialData>({
    url: `/jobs/${jobId}/social/`,
  });
  return response.data;
}

export function editSocial(jobId: number, data: ITODO) {
  return request.post({
    url: `/jobs/${jobId}/social/`,
    data,
  });
}

export type RewardsData = {
  hasRewards: boolean;
  rewardsTitle: string | null;
  rewards: string | null;
};

export async function getRewards(jobId: number) {
  const response = await unhandledRequest.get<RewardsData>({
    url: `/jobs/${jobId}/rewards/`,
  });
  return response.data;
}

export function editRewards(jobId: number, data: ITODO) {
  return request.patch({
    url: `/jobs/${jobId}/rewards/`,
    data,
  });
}

export type EmailTemplateUpdateData = {
  useCompanyEmailTemplate: boolean;
  contentHtml: string;
};

type EmailTemplateBaseData = {
  fromEmail: string;
  subject: string;
  contentHtml: string;
  title: string;
};

export type EmailTemplateData = {
  useCompanyEmailTemplate: boolean;
  jobTemplate?: EmailTemplateBaseData;
  companyTemplate?: EmailTemplateBaseData;
  defaultContent?: string;
  subject: string;
};

export async function getEmailTemplate(jobId: number) {
  const response = await unhandledRequest.get<EmailTemplateData>({
    url: `/jobs/${jobId}/email-template/`,
  });
  return response.data;
}

export async function updateEmailTemplate(jobId: number, data: EmailTemplateUpdateData) {
  return request.patch({
    url: `/jobs/${jobId}/email-template/`,
    data,
  });
}

export async function loadApplicationsCount(
  jobId: number,
  conjunction: FiltersConjunctionType = 'and',
  queryData?: FilterQueryData
) {
  const query = toQueryString({
    limit: 1,
    ...queryData,
    minimal: true,
    job: conjunction === 'and' ? jobId : null,
    fixed__job: conjunction === 'or' ? jobId : null,
  });

  const url = conjunction === 'and' ? '/search/applications/' : '/search/applicationsor/';

  return request.get<ILimitSearchResult<ITODO>>({
    url: url + query,
  });
}

export function loadMinimal() {
  return request.get({
    url: '/search/jobs/' + toQueryString({ limit: 10000, minimal: true }),
  });
}

export function loadMinimalWithoutArchived() {
  const query = toQueryString({
    limit: 10000,
    minimal: true,
    is_archived: false,
  });

  return request.get({
    url: '/search/jobs/' + query,
  });
}

export function load(status: ITODO, size: number, searchStr?: string) {
  let query: Record<string, ITODO> = {
    search_multi_match: searchStr || null,
    limit: Math.max(size, 15),
    list: true,
  };

  switch (status) {
    case 'published':
      query.is_published = true;
      query.is_archived = false;
      query.deadline__gtnowornull = true;
      query.ordering = '-date_published';
      break;
    case 'drafts':
      query.is_published = false;
      query.is_archived = false;
      query.ordering = '-date_created';
      break;
    case 'expired':
      query.is_published = true;
      query.is_archived = false;
      query.deadline__lt = startOfToday().toISOString().split('T')[0];
      query.ordering = '-deadline';
      break;
    case 'archived':
      query.is_archived = true;
      query.ordering = '-date_archived';
      break;
    default:
      break;
  }

  return request.get({
    url: '/search/jobs/' + toQueryString(query),
  });
}

interface ExportData {
  fields: string[];
}

export function exportJobs(queryString: string, data: ExportData) {
  return request.post({
    url: '/search/jobs-export/' + queryString,
    data,
  });
}

export async function search(params: {
  companyId?: number;
  title?: string;
  queryData?: object;
  limit?: number;
}) {
  const { companyId, title, queryData, limit = 8 } = params;

  const query = toQueryString({
    limit,
    title__contains: title ? title.toLowerCase().split(' ') : null,
    ordering: 'title',
    title_only: true,
    is_archived: false,
    company_id: companyId ?? null,
    ...queryData,
  });

  const response = await unhandledRequest.get<JobTitleSearch>({
    url: '/search/jobs/' + query,
  });

  return response.data;
}

export function loadSharing() {
  return request.get({
    url: '/jobs/sharing/',
  });
}

export function moveToDrafts(jobId: number) {
  return request.patch({
    url: `/jobs/${jobId}/`,
    data: { isPublished: false, isArchived: false },
  });
}

export function moveToArchive(jobId: number) {
  return request.post({
    url: `/jobs/${jobId}/archive/`,
  });
}

export async function loadStatistics(jobId: number, from?: string) {
  const response = await unhandledRequest.get<JobStatistics>({
    url: `/jobs/${jobId}/statistics/` + toQueryString({ from: from || null }),
  });
  return response.data;
}

export function unpublish(jobId: number) {
  return request.delete({
    url: `/jobs/${jobId}/publish/`,
  });
}

export function publish(jobId: number) {
  return request.post({
    url: `/jobs/${jobId}/publish/`,
  });
}

export function duplicate(jobId: number) {
  return request.post({
    url: `/jobs/${jobId}/duplicate/`,
  });
}

export function loadIntegrations(jobId: number, integrationType: string) {
  return request.get({
    url: `/jobs/${jobId}/integrations/${integrationType}/`,
  });
}

export function loadIntegration(jobId: number, integrationType: string, integrationId: number) {
  return request.get({
    url: `/jobs/${jobId}/integrations/${integrationType}/${integrationId}/`,
  });
}

export function loadSettings(jobId: number) {
  return request.get({
    url: `/jobs/${jobId}/settings/`,
  });
}

export function updateSettings(jobId: number, data: ITODO) {
  return request.patch({
    url: `/jobs/${jobId}/settings/`,
    data,
  });
}

export function bulkRejectApplicants(
  jobId: number,
  applications: ITODO,
  markNotified: ITODO,
  emailData: ITODO
) {
  let data: Record<string, ITODO> = { markNotified, applications };
  if (emailData) {
    data.emailData = emailData;
  }
  return request.patch({
    url: `/jobs/${jobId}/bulk/reject/`,
    data,
  });
}

export function bulkHireApplicants(jobId: number, applications: number[]) {
  return request.post({
    url: `/jobs/${jobId}/bulk/hire/`,
    data: { applications },
  });
}

export function bulkMessageApplicants(jobId: number, applications: ITODO, emailData: ITODO) {
  return request.patch({
    url: `/jobs/${jobId}/bulk/message/`,
    data: { emailData, applications },
  });
}

export function bulkGrantAccessToApplicants(item: {
  jobId: number;
  applications: string[];
  users: number[];
}) {
  return unhandledRequest.patch({
    url: `/jobs/${item.jobId}/bulk/grant/`,
    data: { applications: item.applications, users: item.users },
  });
}

export function bulkRetractApplicants(jobId: number, applications: number[]) {
  return request.post({
    url: `/jobs/${jobId}/bulk/retract/`,
    data: { applications },
  });
}

export function bulkActiveApplicants(jobId: number, applications: number[]) {
  return request.post({
    url: `/jobs/${jobId}/bulk/active/`,
    data: { applications },
  });
}

export function bulkDeleteApplicants(jobId: number, applications: number[]) {
  return request.post({
    url: `/jobs/${jobId}/bulk/delete/`,
    data: { applications },
  });
}

export function submitIntegration(
  jobId: number,
  integrationType: string,
  integrationId: number,
  data: ITODO
) {
  return request.post({
    url: `/jobs/${jobId}/integrations/${integrationType}/${integrationId}/`,
    data: { data },
  });
}

export function bulkPurgeAccess(user: ITODO, jobId: number) {
  return request.patch({
    url: `/jobs/${jobId}/bulk/purge/`,
    data: { user },
  });
}

export function bulkMoveToStage(jobId: number, applications: [number], stage: number) {
  return request.patch({
    url: `/jobs/${jobId}/bulk/move_stage/`,
    data: {
      applications,
      stageId: stage,
      // TODO: Remove this post jobStage migration since its no longer used
      stage,
    },
  });
}

export function deleteLanguage(jobId: number, langKey: CareerSiteLanguageType) {
  return request.delete({
    url: `/jobs/${jobId}/language/${langKey}/`,
  });
}

export function loadForUserAccess() {
  const query = toQueryString({
    limit: 50,
    is_archived: false,
    ordering: '-date',
  });

  return request.get({
    url: '/search/jobs/' + query,
  });
}
export type UserApplicationAccessForJob = {
  id: number;
  name: string;
  date: string;
  picture?: string;
};

export async function loadApplicationAccessForJob(jobId: number, userId: number) {
  const response = await unhandledRequest.get<UserApplicationAccessForJob[]>({
    url: `/jobs/${jobId}/access/${userId}/`,
  });
  return response.data;
}

export function bulkRemoveApplicationAccess(item: {
  jobId: number;
  users: number[];
  applications: string[];
}) {
  return unhandledRequest.patch({
    url: `/jobs/${item.jobId}/bulk/remove-application-access/`,
    data: { users: item.users, applications: item.applications },
  });
}

export function exportApplicants(data: ExportApplicantsData) {
  return request.post({
    url: `/jobs/${data.jobId}/export/`,
    data: data,
  });
}

export function addNote(data: ITODO) {
  return request.post({
    url: '/jobnotes/',
    data,
  });
}

export function removeNote(jobNoteId: number) {
  return request.delete({
    url: `/jobnotes/${jobNoteId}/`,
  });
}

export function editNote(jobNoteId: number, data: ITODO) {
  return request.patch({
    url: `/jobnotes/${jobNoteId}/`,
    data,
  });
}

export function addAttachment(jobId: number, data: ITODO) {
  return request.post({
    url: `/jobs/${jobId}/attachments/`,
    data,
  });
}

export function removeAttachment(jobId: number, attachmentId: number) {
  return request.delete({
    url: `/jobs/${jobId}/attachments/${attachmentId}/`,
  });
}

export function read(jobId: number, data: ITODO) {
  return request.patch({
    url: `/jobs/${jobId}/read/`,
    data,
  });
}

export async function getJobOrder() {
  const response = await unhandledRequest.get<JobOrderingData[]>({
    url: 'jobs/ordering/',
  });
  return response.data;
}

export async function pinJob(jobId: number, ordering: number) {
  return unhandledRequest.post({
    url: `jobs/${jobId}/pin/`,
    data: { ordering },
  });
}

export async function unpinJob(jobId: number) {
  return unhandledRequest.delete({
    url: `jobs/${jobId}/pin/`,
  });
}
